import { useRef, useState } from 'react'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

interface MergeFieldOption {
  key: string
  label: string
}

interface MarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  mergeFields?: MergeFieldOption[]
  rows?: number
}

type Tab = 'write' | 'preview'

const ToolbarButton: React.FC<{ label: string; onClick: () => void; children: React.ReactNode }> = ({ label, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className="w-7 h-7 flex items-center justify-center text-xs font-semibold rounded hover:bg-gray-200 text-gray-700"
  >
    {children}
  </button>
)

// A lightweight Markdown editor: a formatting toolbar that inserts Markdown
// syntax at the cursor/selection (no contentEditable/rich-text engine, so no
// new heavy WYSIWYG dependency), plus a Write/Preview toggle that renders
// through the same `marked` parser used server-side (see
// backend/src/document-render.ts's renderMarkdownToHtml) so the preview
// matches what actually ends up in the generated PDF.
export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({ value, onChange, mergeFields = [], rows = 16 }) => {
  const [tab, setTab] = useState<Tab>('write')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Wraps the current selection (or inserts an empty pair at the cursor) —
  // used for inline marks like bold/italic.
  const wrapSelection = (before: string, after: string = before) => {
    const textarea = textareaRef.current
    if (!textarea) return
    const { selectionStart, selectionEnd } = textarea
    const selected = value.slice(selectionStart, selectionEnd)
    const next = value.slice(0, selectionStart) + before + selected + after + value.slice(selectionEnd)
    onChange(next)
    requestAnimationFrame(() => {
      textarea.focus()
      textarea.setSelectionRange(selectionStart + before.length, selectionStart + before.length + selected.length)
    })
  }

  // Prefixes the current line — used for block markers like headings/lists.
  const prefixLine = (prefix: string) => {
    const textarea = textareaRef.current
    if (!textarea) return
    const { selectionStart } = textarea
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart)
    onChange(next)
    requestAnimationFrame(() => {
      textarea.focus()
      textarea.setSelectionRange(selectionStart + prefix.length, selectionStart + prefix.length)
    })
  }

  const insertAtCursor = (text: string) => {
    const textarea = textareaRef.current
    if (!textarea) {
      onChange(value + text)
      return
    }
    const { selectionStart, selectionEnd } = textarea
    const next = value.slice(0, selectionStart) + text + value.slice(selectionEnd)
    onChange(next)
    requestAnimationFrame(() => {
      textarea.focus()
      const pos = selectionStart + text.length
      textarea.setSelectionRange(pos, pos)
    })
  }

  const previewHtml = tab === 'preview' ? DOMPurify.sanitize(marked.parse(value, { async: false }) as string) : ''

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-2 py-1.5 flex-wrap">
        <div className="flex items-center gap-0.5 flex-wrap">
          <ToolbarButton label="Bold" onClick={() => wrapSelection('**')}><span className="font-bold">B</span></ToolbarButton>
          <ToolbarButton label="Italic" onClick={() => wrapSelection('_')}><span className="italic">I</span></ToolbarButton>
          <ToolbarButton label="Heading 1" onClick={() => prefixLine('# ')}>H1</ToolbarButton>
          <ToolbarButton label="Heading 2" onClick={() => prefixLine('## ')}>H2</ToolbarButton>
          <ToolbarButton label="Bullet list" onClick={() => prefixLine('- ')}>•</ToolbarButton>
          <ToolbarButton label="Numbered list" onClick={() => prefixLine('1. ')}>1.</ToolbarButton>
          <ToolbarButton label="Callout" onClick={() => prefixLine('> ')}>&ldquo;</ToolbarButton>
          <ToolbarButton label="Table" onClick={() => insertAtCursor('\n\n| | |\n|---|---|\n| | |\n\n')}>▦</ToolbarButton>
          {mergeFields.length > 0 && (
            <select
              onChange={(e) => {
                if (e.target.value) insertAtCursor(`{{${e.target.value}}}`)
                e.target.value = ''
              }}
              className="ml-1 text-xs border border-gray-300 rounded px-1.5 py-1 bg-white text-gray-700"
              defaultValue=""
              aria-label="Insert merge field"
            >
              <option value="" disabled>Insert field…</option>
              {mergeFields.map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
          )}
        </div>

        <div className="flex gap-1 shrink-0">
          {(['write', 'preview'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-2.5 py-1 text-xs font-medium rounded ${tab === t ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t === 'write' ? 'Markdown' : 'Preview'}
            </button>
          ))}
        </div>
      </div>

      {tab === 'write' ? (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className="w-full px-3 py-2 font-mono text-xs focus:outline-none resize-y"
        />
      ) : (
        <div
          className="md-preview px-4 py-3 overflow-y-auto bg-white"
          style={{ minHeight: `${rows * 1.4}rem`, maxHeight: '28rem' }}
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
      )}
    </div>
  )
}
